import groupBy from 'lodash/groupBy';
import minBy from 'lodash/minBy';
import uniq from 'lodash/uniq';

import { formatDateShort } from '~/services/TimeService';

import featureToggle from '~/utils/featureToggle';
import { pluralizeToString } from '~/utils/stringUtils';

import { COPY_SECTIONS, type CopySection, type CopySectionDetails } from '../copyFromPrevious/copyFromPreviousModel';
import type { BlackoutFormValues } from '../tabs/blackouts/types';
import { defaultApplyTo, findReusableInclusionAsset } from '../tabs/inclusions/inclusionsModel';
import type { InclusionFormValues } from '../tabs/inclusions/types';
import { BASIC_CATEGORY_INDEX } from '../tabs/packageOptions/packageCategories';
import type { PreviousDealOptions } from '../tabs/packageOptions/previousDealOptions';
import { DEFAULT_OCCUPANCY } from '../tabs/packageOptions/previousPackageVariants';
import type { CreatedAsset, RoomAssetLink } from '../tabs/packageOptions/types';
import { type TravelWindow, staleRangeReason } from '../tabs/propertySetup/propertySetupModel';

import type {
  ImportIssue,
  ImportSection,
  ParsedBlackout,
  ParsedDealOption,
  ParsedInclusion,
  ParsedWorkbook,
} from './types';

export type ImportSectionKey = Extract<CopySection, 'dealOptions' | 'inclusions' | 'blackouts'>;

const IMPORT_SECTION_KEYS: Array<ImportSectionKey> = ['dealOptions', 'inclusions', 'blackouts'];

export const IMPORT_SECTIONS = IMPORT_SECTION_KEYS.map((key) => ({
  key,
  label: COPY_SECTIONS.find((section) => section.key === key).label,
}));

export type ImportStep = 'workbook' | 'rooms' | 'inclusions' | 'confirm';

export const IMPORT_STEP_LABELS: Record<ImportStep, string> = {
  workbook: 'Workbook',
  rooms: 'Rooms',
  inclusions: 'Inclusions',
  confirm: 'Confirm',
};

export function importSteps(selected: Set<ImportSectionKey>): Array<ImportStep> {
  return [
    'workbook',
    ...(selected.has('dealOptions') ? ['rooms' as const] : []),
    ...(selected.has('inclusions') ? ['inclusions' as const] : []),
    'confirm',
  ];
}

// Borrows the account view's rollout flag (off in prod) until the import has a flag of its own.
export function isExcelImportEnabled(): boolean {
  return featureToggle.availableToShow('ACCOUNT_VIEW_ENABLED');
}

// Only unnamed blackouts are numbered, in the same order as the parser's notes about them.
export function toBlackoutFormValues(blackouts: ReadonlyArray<ParsedBlackout>): Array<BlackoutFormValues> {
  let unnamed = 0;
  return blackouts.map(({ name, startDate, endDate }) => ({
    name: name ?? `Blackout ${(unnamed += 1)}`,
    startDate,
    endDate,
  }));
}

function andList(items: Array<string>): string {
  return items.length === 1 ? items[0] : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

// Errors with a cell are entries the parser rejected; one without a cell means the block itself is missing.
function rejectedCount(workbook: ParsedWorkbook, section: ImportSection): number {
  return workbook.issues.filter((issue) => issue.section === section && issue.severity === 'error' && issue.cell)
    .length;
}

function sectionError(workbook: ParsedWorkbook, section: ImportSection): ImportIssue | undefined {
  return workbook.issues.find((issue) => issue.section === section && issue.severity === 'error' && !issue.cell);
}

// Rows with the same problem share one note, so a sheet of broken names reads as one line rather than eleven.
function issueNotes(workbook: ParsedWorkbook, section: ImportSection): CopySectionDetails['notes'] {
  const blocking = sectionError(workbook, section);
  const issues = workbook.issues.filter((issue) => issue.section === section && issue !== blocking);
  const groups = Object.values(groupBy(issues, (issue) => `${issue.row ? 'rows' : 'own'}::${issue.message}`));
  return groups.map((group) => {
    const rows = group.flatMap(({ row }) => (row ? [String(row)] : []));
    const where = rows.length ? `${rows.length === 1 ? 'Row' : 'Rows'} ${andList(rows)} won't load: ` : '';
    return { severity: 'warning' as const, text: `${where}${group[0].message}` };
  });
}

function emptyMessage(workbook: ParsedWorkbook, section: ImportSection, skipped: string, none: string): string {
  return sectionError(workbook, section)?.message ?? (skipped ? `${skipped} found, none will load.` : none);
}

function withSkipped(summary: string, skipped: number, noun?: string): string {
  return skipped ? `${summary}, ${noun ? pluralizeToString(noun, skipped) : skipped} won't` : summary;
}

const STALE_RANGE_TEXT = {
  outsideTravel: "is outside this Opportunity's travel dates",
  ended: 'has already ended',
};

// Property Setup fills in a few seconds after the page loads; until then only the "already ended" check runs.
const NO_TRAVEL_WINDOW: TravelWindow = { travelFrom: null, travelTo: null };

// Unlike Copy from previous, stale ranges still load: in the Opportunity's own workbook they are likely typos.
export function blackoutsDetails(
  workbook: ParsedWorkbook,
  travelWindow: TravelWindow | null,
  today: string,
): CopySectionDetails {
  const notes = issueNotes(workbook, 'blackouts');
  const skipped = rejectedCount(workbook, 'blackouts');
  if (workbook.blackouts.length === 0) {
    const found = skipped ? pluralizeToString('blackout', skipped) : '';
    return { emptyMessage: emptyMessage(workbook, 'blackouts', found, 'The workbook has no blackouts.'), notes };
  }
  toBlackoutFormValues(workbook.blackouts).forEach((blackout) => {
    const reason = staleRangeReason(blackout, travelWindow ?? NO_TRAVEL_WINDOW, today);
    if (reason) {
      const dates = `${formatDateShort(blackout.startDate)} to ${formatDateShort(blackout.endDate)}`;
      notes.push({
        severity: 'warning',
        text: `The "${blackout.name}" blackout (${dates}) ${STALE_RANGE_TEXT[reason]}. It still loads, so check it on the Blackouts tab.`,
      });
    }
  });
  return {
    summary: withSkipped(`${pluralizeToString('blackout', workbook.blackouts.length)} will load`, skipped),
    notes,
  };
}

export function loadsCosts({ currency }: ParsedWorkbook, netCurrency: string): boolean {
  return currency === netCurrency;
}

export function importedRoomTypes(dealOptions: ReadonlyArray<ParsedDealOption>): Array<string> {
  return uniq(dealOptions.map(({ roomType }) => roomType));
}

function costsNote({ currency }: ParsedWorkbook, netCurrency: string): string {
  return currency
    ? `Net prices won't load: the workbook is in ${currency} and this session is in ${netCurrency}. Set the currency to ${currency} in Property Setup, then upload again.`
    : "Net prices won't load: the workbook doesn't say which currency they're in.";
}

function mixedOccupancyText(roomTypes: Array<string>): string {
  const one = roomTypes.length === 1;
  const rooms = andList(roomTypes.map((roomType) => `"${roomType}"`));
  return `${rooms} ${one ? 'has' : 'have'} a different occupancy for each stay, so ${one ? 'it loads' : 'they load'} as ${DEFAULT_OCCUPANCY}. Check ${one ? 'it' : 'them'} on Package Options.`;
}

export function dealOptionsDetails(workbook: ParsedWorkbook, netCurrency: string): CopySectionDetails {
  const notes = issueNotes(workbook, 'dealOptions');
  const skipped = rejectedCount(workbook, 'dealOptions');
  if (workbook.dealOptions.length === 0) {
    const found = skipped ? pluralizeToString('row', skipped) : '';
    const none = 'The Deal Options Import sheet has no base packages.';
    return { emptyMessage: emptyMessage(workbook, 'dealOptions', found, none), notes };
  }
  if (workbook.skippedVariants.length > 0) {
    // Grouped the way base names are matched, so "IndulgenceUpgrade" counts with "Indulgence Upgrade".
    const variants = Object.values(
      groupBy(workbook.skippedVariants, (variant) => variant.replace(/\s+/g, '').toLowerCase()),
    )
      .map((labels) => `${labels[0]} (${labels.length})`)
      .join(', ');
    notes.push({
      severity: 'info',
      text: `Only base packages load. Add the others on Package Options afterwards: ${variants}.`,
    });
  }
  const mixed = mixedOccupancyRoomTypes(workbook.dealOptions);
  if (mixed.length) {
    notes.push({ severity: 'warning', text: mixedOccupancyText(mixed) });
  }
  if (!loadsCosts(workbook, netCurrency)) {
    notes.push({ severity: 'warning', text: costsNote(workbook, netCurrency) });
  }
  const rooms = pluralizeToString('room', importedRoomTypes(workbook.dealOptions).length);
  const prices = pluralizeToString('price', workbook.dealOptions.length);
  return { summary: withSkipped(`${rooms} will load (${prices})`, skipped, 'row'), notes };
}

// The grid holds one occupancy per row, so a room whose stays name different ones keeps the default.
function roomOccupancy(rows: Array<ParsedDealOption>): string | undefined {
  const occupancies = uniq(rows.map(({ occupancy }) => occupancy ?? DEFAULT_OCCUPANCY));
  return occupancies.length === 1 ? occupancies[0] : undefined;
}

function rowsByRoomType(dealOptions: ReadonlyArray<ParsedDealOption>) {
  return Object.entries(groupBy(dealOptions, 'roomType'));
}

function mixedOccupancyRoomTypes(dealOptions: ReadonlyArray<ParsedDealOption>): Array<string> {
  return rowsByRoomType(dealOptions).flatMap(([roomType, rows]) => (roomOccupancy(rows) ? [] : [roomType]));
}

export function toImportedDealOptions(
  dealOptions: ReadonlyArray<ParsedDealOption>,
  assetByRoomType: Record<string, RoomAssetLink>,
  withCosts: boolean,
): PreviousDealOptions {
  const packages = rowsByRoomType(dealOptions).map(([roomType, rows]) => {
    // The tool holds one allocation and extra night price per row, taken from the shortest stay.
    const shortestStay = minBy(rows, 'nights');
    return {
      asset: assetByRoomType[roomType],
      categoryIndex: BASIC_CATEGORY_INDEX,
      dealIdByLos: {},
      occupancy: roomOccupancy(rows) ?? DEFAULT_OCCUPANCY,
      allocation: shortestStay.allocation,
      sellByLos: Object.fromEntries(rows.map(({ nights, sell }) => [nights, sell])),
      netByLos: withCosts
        ? Object.fromEntries(rows.flatMap(({ nights, net }) => (net === null ? [] : [[nights, net]])))
        : {},
      extraNightSell: shortestStay.extraNightSell ?? undefined,
      extraNightNet: withCosts ? (shortestStay.extraNightNet ?? undefined) : undefined,
    };
  });
  return {
    packages,
    ambiguousCells: 0,
    mixedOccupancyPackages: mixedOccupancyRoomTypes(dealOptions).length,
    otherCurrencyNetCells: 0,
  };
}

const WORKBOOK_RRP_CURRENCY = 'AUD';

export function loadsRrp(sessionCurrency: string): boolean {
  return sessionCurrency === WORKBOOK_RRP_CURRENCY;
}

export function inclusionsDetails(
  workbook: ParsedWorkbook,
  sessionCurrency: string,
  leftOutRows: ReadonlySet<number> = new Set(),
): CopySectionDetails {
  const notes = issueNotes(workbook, 'inclusions');
  const skipped = rejectedCount(workbook, 'inclusions');
  if (workbook.inclusions.length === 0) {
    const found = skipped ? pluralizeToString('row', skipped) : '';
    const none = 'The Inclusions Import sheet has no inclusions.';
    return { emptyMessage: emptyMessage(workbook, 'inclusions', found, none), notes };
  }
  const loading = workbook.inclusions.filter(({ row }) => !leftOutRows.has(row));
  if (!loadsRrp(sessionCurrency) && workbook.inclusions.some(({ rrp }) => rrp !== null)) {
    notes.push({
      severity: 'warning',
      text: `RRP won't load: the workbook gives it in ${WORKBOOK_RRP_CURRENCY} and this session is in ${sessionCurrency}. Add it on Inclusions before saving.`,
    });
  } else {
    const withoutRrp = loading.filter(({ rrp }) => rrp === null).length;
    if (withoutRrp) {
      notes.push({
        severity: 'info',
        text: `${pluralizeToString('inclusion', withoutRrp)} ${withoutRrp === 1 ? 'has' : 'have'} no RRP in the workbook. Add it on Inclusions before saving.`,
      });
    }
  }
  const summary = withSkipped(`${pluralizeToString('inclusion', loading.length)} will load`, skipped, 'row');
  return { summary: leftOutRows.size ? `${summary}, ${leftOutRows.size} left out` : summary, notes };
}

export function toInclusionDrafts(
  inclusions: ReadonlyArray<ParsedInclusion>,
  withRrp: boolean,
): Array<InclusionFormValues> {
  return inclusions.map(({ name, type, description, prv, rrp }) => ({
    pricingOptionIndex: 0,
    name,
    description: description ?? '',
    type: type ?? undefined,
    validFor: [],
    rrp: withRrp ? (rrp ?? undefined) : undefined,
    prv: prv ?? undefined,
    applyTo: defaultApplyTo(),
  }));
}

export type InclusionAssetChoices = Record<number, string | null>;

// Null is a new asset. Same lookup the step shows, so a refetch after review cannot change what Load links.
export function reviewInclusionAssets(
  inclusions: ReadonlyArray<ParsedInclusion>,
  sessionAssets: Array<CreatedAsset>,
  accountAssets: Array<RoomAssetLink>,
): InclusionAssetChoices {
  return Object.fromEntries(
    inclusions.map(({ row, name }) => [
      row,
      findReusableInclusionAsset(name, sessionAssets, accountAssets)?.id ?? null,
    ]),
  );
}

export function inclusionRowsWithMissingAsset(
  choices: InclusionAssetChoices,
  sessionAssets: Array<CreatedAsset>,
  accountAssets: Array<RoomAssetLink>,
): Array<number> {
  const listed = new Set([...sessionAssets, ...accountAssets].map(({ id }) => id));
  return Object.entries(choices)
    .filter(([, assetId]) => assetId !== null && !listed.has(assetId))
    .map(([row]) => Number(row));
}

export function missingAssetMessages(roomTypes: Array<string>, inclusionNames: Array<string>): Array<string> {
  return [
    ...(roomTypes.length
      ? [`The room picked for ${andList(roomTypes)} is no longer on this property. Go back to Rooms and pick again.`]
      : []),
    ...(inclusionNames.length
      ? [
          `The asset for ${andList(inclusionNames)} is no longer on this property. Go back to Inclusions and check again.`,
        ]
      : []),
  ];
}
